import { SearchState } from '../SearchPage';
import {
  DEFAULT_SORT_STATE,
  isValidSortState,
  removeSourceOnlySortField,
  SOURCE_ONLY_SORT_FIELDS,
  type SortState,
} from '../sortState';

import {
  createSavedSearchStore,
  repaired,
  SavedSearchEntry,
  RepairedState,
} from 'src/common/savedSearches';

/**
 * Saved searches for the Search page.
 *
 * The read/write/evict/notify mechanics live in the shared store — see
 * `src/common/savedSearches/savedSearchStore.ts`, which also documents the
 * TEMPORARY localStorage persistence. Only what is specific to this page is
 * here: the storage key, how a stored SearchState is validated (including the
 * repair-vs-drop policy below), and the label wording.
 */

const SAVED_SEARCHES_KEY = 'LangHub-savedSearches';

/** Fired after a write so the bar refreshes in the same tab. */
const SAVED_SEARCHES_CHANGED = 'langhub-saved-searches-changed';

export type SavedSearch = SavedSearchEntry<SearchState>;

/**
 * Validates a saved SearchState, repairing what can be repaired and rejecting
 * what cannot. Returns null for entries that must be dropped, and
 * `repaired(state)` when a recoverable defect was corrected so the store keeps
 * the entry and rewrites storage canonically.
 *
 * Where the recoverable / unrecoverable line is drawn
 * ---------------------------------------------------
 * The presence of the `sortState` KEY is the schema-generation marker. An entry
 * that has it was written by a build that already modelled sort as its own
 * object, so a bad `sortState` VALUE is a localized defect: sort order is a
 * presentation choice that is fully reconstructible from `DEFAULT_SORT_STATE`,
 * while the query text, filters and mode — the things the user actually saved —
 * are still meaningful. Those entries are kept with the sort reset.
 *
 * RECOVERABLE (keep the entry, reset sort to `DEFAULT_SORT_STATE` — i.e. no
 * explicit sort) — anything wrong with the `sortState` value alone:
 * - a field the UI does not offer (`LAST_UPDATED` / `CREATED`; note
 *   `STRING_GROUP` is offered again now that the backend can sort by it),
 * - the previous MULTI-COLUMN array shape, which no longer has a meaning now
 *   that exactly one criterion is modelled,
 * - an unknown sort order, or any other value that is neither `null` nor a valid
 *   `{ field, order }`.
 *
 * Note `null` is now a VALID stored value, not a defect: it is the unsorted
 * default, and an entry saved while nothing was sorted round-trips unchanged.
 *
 * UNRECOVERABLE (drop the entry) — a defect outside `sortState`, or a signal
 * that the entry predates the current contract:
 * - `sortState` KEY ABSENT: the entry comes from a different schema
 *   generation — the pre-multi-column `sortBy`/`sortOrder` pair, or a partial
 *   write. Its remaining fields type-check but cannot be trusted to still MEAN
 *   what they say (filter shapes and mode semantics have changed since), so it
 *   is not resurrected on the strength of a type check alone.
 * - `queryText`, `mode` or `exactMatch` invalid: the saved search's own
 *   identity is gone, and unlike sort there is no safe value to substitute —
 *   guessing one would silently hand the user a search they never saved.
 *
 * Separately, `filters` stays permissive (any object is accepted, a non-object
 * becomes `{}`) — unchanged pre-existing behavior.
 */
function migrateSearchState(
  state: unknown,
): SearchState | RepairedState<SearchState> | null {
  if (!state || typeof state !== 'object') return null;

  const s = state as Record<string, unknown>;

  /*
   * Schema-generation gate. Must run BEFORE the sort coercion below, otherwise
   * a legacy `sortBy`/`sortOrder` entry would be silently adopted with a reset
   * sort instead of dropped.
   */
  if (!('sortState' in s)) return null;

  // Unrecoverable: required fields with no safe substitute.
  if (typeof s.queryText !== 'string') return null;
  if (s.mode !== 'SOURCE' && s.mode !== 'TRANSLATION') return null;
  if (typeof s.exactMatch !== 'boolean') return null;

  const mode = s.mode as SearchState['mode'];

  // Recoverable: an unusable sortState value falls back to the default.
  const rawSortState = s.sortState;
  const sortStateValid = isValidSortState(rawSortState);
  const validatedSortState: SortState = sortStateValid
    ? rawSortState
    : DEFAULT_SORT_STATE;

  /*
   * Cross-consistency, not just per-value validity: `isValidSortState` accepts
   * any known field, but the backend rejects the source-only ones for
   * TRANSLATION searches (`STATUS` is source-string-only per the Smithy
   * contract). A stored `TRANSLATION` + `STATUS` pair therefore passes value
   * validation, auto-triggers a search on load and comes back as a
   * ValidationException. It cannot be produced by the live UI —
   * `handleModeChange` strips those fields on every mode switch — only by
   * tampered or bug-era storage, which is exactly what this validator is for.
   *
   * The unsorted state (`null`) has no field, so there is nothing to strip.
   */
  const strippedForMode =
    mode === 'TRANSLATION' &&
    validatedSortState !== null &&
    SOURCE_ONLY_SORT_FIELDS.includes(validatedSortState.field);
  const sortState = strippedForMode
    ? removeSourceOnlySortField(validatedSortState)
    : validatedSortState;

  // Validate filters (permissive — accept any object shape, strip unknowns)
  const filters =
    s.filters && typeof s.filters === 'object'
      ? (s.filters as SearchState['filters'])
      : {};

  const migrated: SearchState = {
    queryText: s.queryText as string,
    mode,
    exactMatch: s.exactMatch as boolean,
    filters,
    sortState,
  };

  return !sortStateValid || strippedForMode ? repaired(migrated) : migrated;
}

export function generateLabel(state: SearchState): string {
  const parts: string[] = [];
  if (state.queryText) {
    parts.push(`"${state.queryText}"`);
  }
  if (state.filters.namespaceIds?.length) {
    parts.push(`ns:${state.filters.namespaceIds.join(',')}`);
  }
  if (state.mode === 'TRANSLATION' && state.filters.translatedLocales?.length) {
    parts.push(`locale:${state.filters.translatedLocales.join(',')}`);
  }
  if (parts.length === 0) {
    parts.push(
      state.mode === 'SOURCE' ? 'Source browse' : 'Translation browse',
    );
  }
  return parts.join(' ').slice(0, 50);
}

const store = createSavedSearchStore<SearchState>({
  storageKey: SAVED_SEARCHES_KEY,
  changedEvent: SAVED_SEARCHES_CHANGED,
  parseState: migrateSearchState,
  generateLabel,
});

/** Event the bar subscribes to for same-tab updates. */
export const SAVED_SEARCHES_CHANGED_EVENT = store.changedEvent;

/** Number of saved searches kept before the oldest is evicted. */
export const MAX_SAVED_SEARCHES = store.maxEntries;

export function getSavedSearches(): SavedSearch[] {
  return store.getAll();
}

/**
 * Saves a search with the given user-provided name.
 * If no name is provided, falls back to auto-generated label.
 */
export function saveSearch(state: SearchState, name?: string): void {
  store.save(state, name);
}

export function removeSavedSearch(id: string): void {
  store.remove(id);
}
